import React, { useCallback, useEffect, useState } from 'react';
import ViolationStateBlock from '../components/ViolationStateBlock';
import ChangeRequestCard from './ChangeRequestCard';
import ChangeRequestDetailModal from './ChangeRequestDetailModal';
import {
  approveChangeRequest,
  listAdminChangeRequests,
  rejectChangeRequest,
} from '../../../services/analyticsChangeRequests';
import { describeHttpError } from '../../../utils/apiError';
import { describeConflict } from '../../../utils/conflictMessages';
import { useUrlState } from '../../../hooks/useUrlState';
import { useCameraNames } from '../../../hooks/useCameraNames';
import { APP_EVENTS, emitAppEvent } from '../../../utils/appEvents';
import {
  CHANGE_REQUEST_STATUS_OPTIONS,
  DEFAULT_CHANGE_REQUEST_LIMIT,
} from '../../../constants/analyticsConstants';
import { useAutoDismiss } from '../../../hooks/useAutoDismiss';

// FE-FILTER02: схема — стабильная ссылка на уровне модуля.
const CHANGE_REQUESTS_URL_SCHEMA = {
  status: { param: 'crStatus', default: 'pending' },
};

/**
 * Разбор заявок клиента администратором (Part 4, раздел 7.2–7.4).
 *
 * Заявка применяется только ответом backend. Любой 409 — это «не применено»:
 * список перечитывается, заявка помечается устаревшей, решение остаётся за
 * человеком. Автоматического повтора нет и быть не должно.
 */
const ChangeRequestsTab = ({ onApplied }) => {
  // FE-FILTER02: фильтр статуса живёт в URL, чтобы reload/Back сохраняли выбор.
  const [url, setUrl] = useUrlState(CHANGE_REQUESTS_URL_SCHEMA);
  const { status } = url;
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [phase, setPhase] = useState('loading'); // loading | ready | error
  const [errorMessage, setErrorMessage] = useState('');

  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState('');
  const [stale, setStale] = useState(false);
  const [notice, setNotice] = useState('');

  // FE-FIX08: «Заявка одобрена/отклонена» — success-уведомление, гаснет само.
  useAutoDismiss(notice, setNotice);

  const load = useCallback(() => {
    setPhase('loading');

    return listAdminChangeRequests({ status: status || undefined, limit: DEFAULT_CHANGE_REQUEST_LIMIT })
      .then((response) => {
        setItems(response.items);
        setTotal(response.total);
        setPhase('ready');
      })
      .catch((error) => {
        setItems([]);
        setTotal(0);
        setErrorMessage(describeHttpError(error, 'Не удалось загрузить заявки'));
        setPhase('error');
      });
  }, [status]);

  useEffect(() => { load(); }, [load]);

  // ТЗ 29 / finding 5: список и карточка заявки должны показывать имя камеры,
  // а не только её id — заявки не всегда несут camera_name сами. Общий кэш
  // useCameraNames уже умеет пропускать элементы с готовым camera_name.
  const { cameraLabel: cameraNameById } = useCameraNames(items);
  const cameraLabel = (request) => request.camera_name || cameraNameById(request.camera_id);

  const openRequest = (request) => {
    setSelected(request);
    setActionError('');
    setStale(request.status === 'stale' || request.status === 'conflict');
  };

  const closeRequest = () => {
    setSelected(null);
    setActionError('');
    setStale(false);
  };

  const handleApprove = () => {
    setBusy(true);
    setActionError('');

    approveChangeRequest(selected.change_request_id)
      .then(() => {
        setNotice('Заявка одобрена и применена');
        closeRequest();
        if (onApplied) onApplied();
        // ТЗ 27: badge в Sidebar/на вкладке обязан обновиться сразу же, а не
        // ждать следующего visibilitychange.
        emitAppEvent(APP_EVENTS.CHANGE_REQUESTS_REFRESH);
        return load();
      })
      .catch((error) => {
        if (error?.response?.status === 409) {
          // Backend оставляет заявку неприменённой: конфигурация уехала вперёд
          // или заявку уже разобрали. Ни автоповтора, ни вида «одобрено».
          setStale(true);
          setActionError(describeConflict(error, 'change_request').message);
          return load();
        }
        setActionError(describeHttpError(error, 'Не удалось одобрить заявку'));
        return undefined;
      })
      .finally(() => setBusy(false));
  };

  const handleReject = (reason) => {
    setBusy(true);
    setActionError('');

    rejectChangeRequest(selected.change_request_id, { reason })
      .then(() => {
        setNotice('Заявка отклонена');
        closeRequest();
        emitAppEvent(APP_EVENTS.CHANGE_REQUESTS_REFRESH);
        return load();
      })
      .catch((error) => {
        setActionError(describeHttpError(error, 'Не удалось отклонить заявку'));
      })
      .finally(() => setBusy(false));
  };

  const renderBody = () => {
    if (phase === 'loading') return <ViolationStateBlock state="loading" />;
    if (phase === 'error') {
      return <ViolationStateBlock state="error" message={errorMessage} onRetry={load} />;
    }
    if (items.length === 0) {
      return <ViolationStateBlock state="empty" message="Заявок по выбранному фильтру нет." />;
    }

    return (
      <ul className="vio-cr-list">
        {items.map((request) => (
          <ChangeRequestCard
            key={request.change_request_id}
            request={request}
            cameraLabel={cameraLabel(request)}
            onOpen={openRequest}
          />
        ))}
      </ul>
    );
  };

  return (
    <>
      <div className="vio-card">
        <div className="vio-filters-grid">
          <div className="vio-field">
            <label className="vio-field-label" htmlFor="vio-cr-status">Статус</label>
            <select
              id="vio-cr-status"
              value={status}
              onChange={(event) => setUrl({ status: event.target.value })}
            >
              {CHANGE_REQUEST_STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
          <div className="vio-field">
            <span className="vio-field-label">Всего заявок</span>
            <span className="vio-readonly-value">{total}</span>
          </div>
        </div>
      </div>

      {notice && <div className="vio-card"><p className="vio-notice">{notice}</p></div>}

      {renderBody()}

      {selected && (
        <ChangeRequestDetailModal
          request={selected}
          busy={busy}
          error={actionError}
          stale={stale}
          cameraLabel={cameraLabel(selected)}
          onApprove={handleApprove}
          onReject={handleReject}
          onClose={closeRequest}
        />
      )}
    </>
  );
};

export default ChangeRequestsTab;
